<template>
  <client-only>
    <div>
      <VCupertino
        v-if="isDefaultPopupOpen"
        id="default-popup"
        :is-show="isDefaultPopupOpen"
        :drawer-options="{
          cssClass:'default-popup__cup',
          modal:$viewport.isGreaterOrEquals('md'),
        }"

        @backdropTap="close"
        @didDismiss="close"
      >
        <template #default>
          <div class="default-popup__root">
            <div class="title h2">{{ defaultPopupState.title || 'Обратный звонок' }}</div>
            <slot name="top"></slot>
            <div class="form">
              <div>
                <input
                  v-model="form.name"
                  placeholder="Ваше имя *"
                  class="input"
                >
                <div
                  v-for="(error, index) in $v.name.$errors"
                  :key="index"
                  class="error-valid"
                >
                  {{ error.$message }}
                </div>
              </div>
              <div>
                <input
                  v-model="form.phone"
                  v-maska
                  placeholder="+7(___) ___-__-__"
                  class="input"
                  data-maska="+7(F##) ###-##-##"
                  data-maska-tokens="F:[0,3,4,9]"
                >
                <div
                  v-for="(error, index) in $v.phone.$errors"
                  :key="index"
                  class="error-valid"
                >
                  {{ error.$message }}
                </div>
              </div>
              <div>
                <textarea
                  v-model="form.message"
                  placeholder="Напишите обратную связь "
                  class="textarea"
                />
              </div>
            </div>
            <LoadingImage
              v-if="defaultPopupState.isNeedUploadImages"
              v-model="form['attachments[]']"
            />
            <button
              :disabled="isLoadingSend"
              class="btn btn-primary"
              @click="onSend"
            >
              {{ defaultPopupState?.btnLabel || 'Отправить' }}
            </button>
          </div>
        </template>
      </VCupertino>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import VCupertino from '~/components/widget/VCupertino.vue';
import type { FormSendType } from '~/types/common';
import LoadingImage from '~/components/common/default-popup/LoadingImage.vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, minLength, required } from '@vuelidate/validators';

const $viewport = useViewport();
const { $api, $toast } = useNuxtApp();
const isLoadingSend = ref<boolean>(false);

const { onCloseDefaultPopup, isDefaultPopupOpen, defaultPopupState } = useDefaultPopup();
type NewFormType = Omit<FormSendType, 'attachments'> & {
	'attachments[]': File[];
	name: string;
	phone: string;
	type: string;
}

const form = reactive<NewFormType>({
  type: defaultPopupState.value.type || 'Обратный звонок',
  name: '',
  phone: '',
  'attachments[]': []
});

const rules = {
  name: {
    required: helpers.withMessage('Обязательно к заполнению', required)
  },

  phone: {
    required: helpers.withMessage('Обязательно к заполнению', required),
    minLength: helpers.withMessage('Неверный формат', minLength(17))
  }
};
const $v = useVuelidate(rules, form);

function close () {
  onCloseDefaultPopup();
  $v.value.$reset();
}

async function onSend () {
  const isFormCorrect = await $v.value.$validate();
  if (!isFormCorrect) {
    return false;
  }
  isLoadingSend.value = true;
  const body: NewFormType = { ...form };

  const formData = new FormData();
  formData.append("url", window.location.href);
  const keys = Object.keys(body);

  keys.forEach((key) => {
    if (body[key as keyof NewFormType] && key !== 'attachments[]') {
      formData.append(key as keyof NewFormType, JSON.stringify(body[key as keyof NewFormType])!);
    }
  });

  body['attachments[]'].forEach((file) => {
    formData.append('attachments[]', file);
  });

  const responseSend = await $api.agent.post('form/send', {
    body: formData
  })
    .then((res) => res)
    .catch((err) => {
      isLoadingSend.value = false;
      const error = errorParserServerResponse(err.response);
      console.log(error);
      $toast.error(error);

      return null;
    });

  if (!responseSend) {
    isLoadingSend.value = false;
    return null;
  }

  $toast.success('Заявка успешно отправлена');
  close();
  form['attachments[]'] = [];
  form.phone = '';
  form.name = '';
}
</script>

<style scoped lang="scss">
.default-popup__root {
	padding: 26px 40px 40px;

	& .title {
		margin-bottom: 32px;
	}

	& .form {
		display: flex;
		flex-direction: column;
		gap: 24px;

		& > div {
			display: flex;
			flex-direction: column;
		}
	}

	& .input {
		height: 60px;
		border-radius: 16px;
	}

	& .textarea {
		height: 130px;
	}

	& .btn {
		margin-top: 32px;
		width: 100%;
		height: 60px;
	}
}

@media (max-width: 860px) {
	.default-popup__root {
		padding: 16px;

		& .title {
			margin-bottom: 16px;
		}

		& .input {
			height: 48px;
			border-radius: 16px;
		}

		& .form {
			display: flex;
			flex-direction: column;
			gap: 12px;
		}

		& .textarea {
			height: 100px;
		}

		& .btn {
			margin-top: 16px;
			width: 100%;
			height: 48px;
		}
	}
}
</style>

<style lang="scss">
.default-popup__cup {
	z-index: 100;

	& .pane {
		max-width: 600px;
		max-height: calc(100dvh - 44px);
	}
}

@supports not(	max-height: calc(100dvh - 44px)) {
	.default-popup__cup {
		& .pane {

			max-height: calc(100vh - 44px);
		}
	}
}
</style>
